// THE MENU — one panel of tiles instead of a rail of buttons.
//
// The owner sent the competitor's version: an Exit bar across the top, then a
// row of three square tiles, then a row of two wide ones, all under a single
// button. "You could mix multiple things into the menu... and we could save
// some space just like our competitor."
//
// WHAT THAT ACTUALLY BUYS. My Bets and How-to-play used to be their own buttons
// in the right dock. Folded in here, the dock goes from three round buttons to
// one — and a player looking for anything that is not a bet now has exactly one
// place to look instead of scanning a column. A tile with a word under it is
// also self-explaining in a way a bare icon never is, which matters more here
// than the pixels do.
//
// Built for roulette rather than added to shared-ui's GameSettingsMenu: that
// component is used by Ludo, X&O and Rummy, and the right change for this game
// is the wrong change for a board game with no table, no voice and no results
// history. Nothing shared was touched.

import { press } from './press';

function Tile({ label, wide, accent, on, state, badge, icon, onTap }: {
  label: string;
  wide?: boolean;
  accent?: boolean;
  /** for the toggles — drives the lit state and, by default, the On/Off line */
  on?: boolean;
  /** overrides the On/Off line. Music uses it to show the tune's name instead. */
  state?: string;
  badge?: string;
  icon: React.ReactNode;
  onTap: () => void;
}) {
  return (
    <button type="button"
            className={`rlt-tile ${wide ? 'rlt-tile--wide' : ''} ${accent ? 'rlt-tile--accent' : ''} ${on === false ? 'rlt-tile--off' : ''}`}
            {...press(onTap)}>
      <span className="rlt-tile-icon">{icon}</span>
      <span className="rlt-tile-label">{label}</span>
      {(state ?? on) !== undefined && (
        <span className="rlt-tile-state">{state ?? (on ? 'On' : 'Off')}</span>
      )}
      {badge && <span className="rlt-tile-badge">{badge}</span>}
    </button>
  );
}

/* The icons are inline SVG at a common 24-unit box, so they all sit on one
   optical weight — a mixed set of icon-font glyphs and SVGs never does. */
const I = {
  exit: (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M10 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h4v-2H6V6h4z" />
      <path d="M14.6 7.4 13.2 8.8 15.4 11H9v2h6.4l-2.2 2.2 1.4 1.4L19.4 12z" />
    </svg>
  ),
  rules: (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 15h-2v-2h2zm1.8-6.3-.9.9c-.6.6-.9 1.1-.9 2.4h-2v-.5c0-1 .4-1.9 1.1-2.5l1.2-1.2c.4-.4.6-.9.5-1.5-.1-.7-.7-1.2-1.4-1.3-.9-.1-1.7.5-1.9 1.3H8.6C8.8 6.5 10.2 5.2 12 5.2c1.9 0 3.5 1.4 3.6 3.2.1.9-.3 1.7-.8 2.3z" />
    </svg>
  ),
  sound: (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M4 9v6h4l5 5V4L8 9H4z" />
      <path d="M16.5 12a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4z" />
      <path d="M14 3.2v2.1a6.8 6.8 0 0 1 0 13.4v2.1a8.9 8.9 0 0 0 0-17.6z" />
    </svg>
  ),
  music: (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M9 17.2V6.3l10-2v10.9h-2V6.7l-6 1.2v9.3z" />
      <ellipse cx="7" cy="17.6" rx="2.9" ry="2.4" />
      <ellipse cx="17" cy="15.6" rx="2.9" ry="2.4" />
    </svg>
  ),
  voice: (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M18 11a6 6 0 0 1-12 0H4a8 8 0 0 0 7 7.9V22h2v-3.1A8 8 0 0 0 20 11h-2z" />
      <rect x="9" y="2" width="6" height="11" rx="3" />
    </svg>
  ),
  bets: (
    <svg viewBox="0 0 24 24" aria-hidden>
      <rect x="3" y="4" width="18" height="3.2" rx="1.4" />
      <rect x="3" y="10.4" width="18" height="3.2" rx="1.4" />
      <rect x="3" y="16.8" width="12" height="3.2" rx="1.4" />
    </svg>
  ),
};

export function RouletteMenu({
  open, onClose, soundOn, voiceOn, musicOn, musicName, voiceNote,
  onToggleSound, onToggleVoice, onToggleMusic, onRules, onMyBets, onExit, onExitGame, exitLabel,
}: {
  open: boolean;
  onClose: () => void;
  soundOn: boolean;
  voiceOn: boolean;
  musicOn: boolean;
  /** which tune is playing — the tile shows it in place of "On" */
  musicName: string;
  /** what the Voice tile reports — the chosen voice, or why there is none */
  voiceNote: string;
  onToggleSound: () => void;
  onToggleVoice: () => void;
  onToggleMusic: () => void;
  /** leave the game entirely (the games list) — distinct from Change table */
  onExitGame?: () => void;
  onRules: () => void;
  onMyBets: () => void;
  onExit: () => void;
  exitLabel: string;
}) {
  if (!open) return null;
  return (
    <div className="rlt-menu-scrim" {...press(onClose)}>
      {/* stopPropagation via a plain handler: press() is for taps that DO
          something, and swallowing the scrim's tap is the opposite of that. */}
      <div className="rlt-menu" onPointerDown={(e) => e.stopPropagation()}>
        <button type="button" className="rlt-menu-x" {...press(onClose)} aria-label="Close">
          <svg viewBox="0 0 24 24" aria-hidden>
            <path d="M18.3 5.7 12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7l1.4-1.4 6.3 6.3 6.3-6.3z" />
          </svg>
        </button>

        {/* EXIT ON TOP, full width — the competitor's shape, and the right one:
            it is the only thing in here that ends the session, so it gets its
            own line rather than sitting in a grid where it can be hit by
            accident. */}
        <Tile wide accent label={exitLabel} icon={I.exit} onTap={onExit} />

        {/* THE THREE AUDIO SWITCHES, TOGETHER. Music moved Rules out of this row
            rather than squeezing in as a fourth: four tiles across a phone starts
            truncating the labels, and more to the point these three are one
            category. A player who wants the table quiet reaches here once.
            They stay three SEPARATE switches, though — wanting the chips without
            the bed, or the bed without the announcer, are all real preferences. */}
        <div className="rlt-menu-row">
          <Tile label="Sounds" icon={I.sound} on={soundOn} onTap={onToggleSound} />
          {/* The Music tile CYCLES rather than toggles: Off → each tune → Off,
              with the name where the other tiles say On. One control, no extra
              screen, and a player who only wants silence still gets there by
              tapping — the same gesture a plain toggle would need. */}
          <Tile label="Music" icon={I.music} on={musicOn}
                state={musicOn ? musicName : 'Off'} onTap={onToggleMusic} />
          {/* THE VOICE TILE DOUBLES AS ITS OWN TEST. Tapping it speaks a sample
              — which is the only way to unlock speech on iOS, because iOS will
              speak from inside a gesture even after refusing every timer-driven
              call of the session. The note under it says which voice was picked,
              so a silent phone can say WHY instead of just being silent. */}
          <Tile label="Voice" icon={I.voice} on={voiceOn} onTap={onToggleVoice} />
        </div>

        <Tile wide label="Rules" icon={I.rules} onTap={onRules} />

        {/* THE DOOR OUT. Change table (top) goes back to the lobby; this
            leaves roulette altogether — the owner is right that a player
            should never need two hops to leave a game. */}
        {onExitGame && <Tile wide accent label="Exit game" icon={I.exit} onTap={onExitGame} />}

        {/* MY BETS IS NOT HERE ANY MORE — it is a line inside the Record rail,
            which is where a player is already looking when they think about
            history. Two doors to one room is the scatter this menu exists to
            undo; the rail is the better door because it is on screen the whole
            time instead of two taps deep. */}

        {voiceNote && <div className="rlt-menu-note">{voiceNote}</div>}
      </div>
    </div>
  );
}
